import type { SenderCountrySelection } from 'Modals/CreateReturnLabelModal/CreateReturnLabel.types'

import {
  createSelectOption,
  getFullContactName,
  isAnonymisedContact,
  parseStreetString,
  truncateToMaxLength,
} from 'Utilities'

import type { getReturnCase_returnCaseItem_employee } from 'Constants/graphql/queries/detail/__generated__/getReturnCase'
import type { getEmployee_employee } from 'Constants/graphql/queries/employee/__generated__/getEmployee'
import type { getOrganisation_organisation } from 'Constants/graphql/queries/organisation/__generated__/getOrganisation'
import { RETURN_LABEL_NAME_MAX_LENGTH } from 'Constants/returnLabel'

import {
  PortalReturnLabelAddressSourceEnum,
  type PortalReturnLabelModalFormSchema,
} from './PortalReturnLabelModal.types'

type CountryList = Record<string, string>

interface AddressLike {
  city: string | null
  country: string | null
  name?: string | null
  street: string | null
  zipcode: string | null
}

// Duck-typed so the modal can pass either an active admin organisation or the
// employee's organisation as the office-address fallback source.
interface OfficeFallbackOrganisation {
  contact: { address: AddressLike | null } | null
  fullName: string
}

const emptyAddressFormValues: Omit<
  PortalReturnLabelModalFormSchema,
  'addressSource'
> = {
  addedReturnCases: [],
  city: '',
  country: null,
  email: '',
  fullName: '',
  phoneNumber: '',
  street: '',
  streetNumber: '',
  zipcode: '',
}

const buildAddressFormValues = (
  addressSource: PortalReturnLabelAddressSourceEnum,
  name: string,
  address: AddressLike,
  countryList: CountryList,
): PortalReturnLabelModalFormSchema => {
  const { streetName, streetNumber } = parseStreetString(address.street || '')
  const countryCode = address.country || ''

  return {
    ...emptyAddressFormValues,
    addressSource,
    city: address.city || '',
    country: countryCode
      ? createSelectOption({
          label: countryList[countryCode] || countryCode,
          value: countryCode,
        })
      : null,
    // SM-2442: office and organisation names come from stored records and
    // routinely exceed the DHL name limit. The office source renders this field
    // disabled, so an over-long prefill would raise a validation error the user
    // cannot clear - cut it at prefill time instead.
    fullName: truncateToMaxLength(name, RETURN_LABEL_NAME_MAX_LENGTH),
    street: streetName || '',
    streetNumber: streetNumber || '',
    zipcode: address.zipcode || '',
  }
}

export const getPortalReturnLabelOfficeAddress = (
  returnCaseEmployee: getReturnCase_returnCaseItem_employee | null,
  organisation: OfficeFallbackOrganisation | null,
  countryList: CountryList,
): PortalReturnLabelModalFormSchema => {
  const office = returnCaseEmployee?.office
  const officeAddress = office?.address

  if (officeAddress) {
    return buildAddressFormValues(
      PortalReturnLabelAddressSourceEnum.OFFICE,
      officeAddress.name || office.locationName || '',
      officeAddress,
      countryList,
    )
  }

  const organisationAddress = organisation?.contact?.address

  if (organisationAddress) {
    return buildAddressFormValues(
      PortalReturnLabelAddressSourceEnum.OFFICE,
      organisationAddress.name || organisation.fullName || '',
      organisationAddress,
      countryList,
    )
  }

  return {
    ...emptyAddressFormValues,
    addressSource: PortalReturnLabelAddressSourceEnum.OFFICE,
  }
}

export const getPortalReturnLabelCustomAddress = (
  activeEmployee: getEmployee_employee | null,
  activeOrganisation: getOrganisation_organisation | null,
  senderCountry: SenderCountrySelection | null,
): PortalReturnLabelModalFormSchema => {
  const isOrganisationContext = Boolean(activeOrganisation?.id)

  if (isOrganisationContext || !activeEmployee?.contact) {
    return {
      ...emptyAddressFormValues,
      addressSource: PortalReturnLabelAddressSourceEnum.CUSTOM,
      country: senderCountry,
    }
  }

  const { contact } = activeEmployee

  // the encryption replaces every field of the employee's own address, so there is nothing left to
  // prefill a sender from - the form opens empty rather than carrying markers into its inputs
  if (!contact.address || isAnonymisedContact(contact)) {
    return {
      ...emptyAddressFormValues,
      addressSource: PortalReturnLabelAddressSourceEnum.CUSTOM,
      country: senderCountry,
      fullName: truncateToMaxLength(
        getFullContactName(contact),
        RETURN_LABEL_NAME_MAX_LENGTH,
      ),
    }
  }

  const { streetName, streetNumber } = parseStreetString(
    contact.address.street || '',
  )

  return {
    addedReturnCases: [],
    addressSource: PortalReturnLabelAddressSourceEnum.CUSTOM,
    city: contact.address.city || '',
    country: senderCountry,
    fullName: truncateToMaxLength(
      contact.address.name || getFullContactName(contact),
      RETURN_LABEL_NAME_MAX_LENGTH,
    ),
    street: streetName || '',
    streetNumber: streetNumber || '',
    zipcode: contact.address.zipcode || '',
  }
}
